// Image helpers for the business card scanner: crop the camera frame to the card
// guide, shrink large photos before OCR, and turn photos upright.

import { autoLevels, flatSize, solveHomography, warpPixels } from './cardGeometry';

export const CARD_RATIO = 1.75; // standard business card, 85 x 55 mm
const MAX_SIDE = 1600;
const JPEG_QUALITY = 0.85;

// The video is shown with object-fit: cover, so only part of the camera frame is
// visible in the box. Map the guide rectangle (in box pixels) back to camera pixels,
// widened by `margin` so the card edges are not cut off, and clamped to the frame.
export const guideToVideoRect = ({ videoWidth, videoHeight, boxWidth, boxHeight, guide, margin = 0.06 }) => {
  const scale = Math.max(boxWidth / videoWidth, boxHeight / videoHeight);
  const offsetX = (boxWidth - videoWidth * scale) / 2;
  const offsetY = (boxHeight - videoHeight * scale) / 2;

  const padX = guide.width * margin;
  const padY = guide.height * margin;
  const left = Math.max(0, (guide.x - padX - offsetX) / scale);
  const top = Math.max(0, (guide.y - padY - offsetY) / scale);
  const right = Math.min(videoWidth, (guide.x + guide.width + padX - offsetX) / scale);
  const bottomEdge = Math.min(videoHeight, (guide.y + guide.height + padY - offsetY) / scale);

  return {
    x: Math.round(left),
    y: Math.round(top),
    width: Math.round(right - left),
    height: Math.round(bottomEdge - top),
  };
};

// Fit width x height inside a maxSide x maxSide box without enlarging.
export const fitWithin = (width, height, maxSide = MAX_SIDE) => {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image'));
    img.src = src;
  });

const drawToDataUrl = (source, sx, sy, sw, sh) => {
  const size = fitWithin(sw, sh);
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  canvas.getContext('2d').drawImage(source, sx, sy, sw, sh, 0, 0, size.width, size.height);
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
};

// Crop the live video to the on-screen card guide.
export const captureGuideArea = (video, guideEl) => {
  const box = video.getBoundingClientRect();
  const g = guideEl.getBoundingClientRect();
  const rect = guideToVideoRect({
    videoWidth: video.videoWidth,
    videoHeight: video.videoHeight,
    boxWidth: box.width,
    boxHeight: box.height,
    guide: { x: g.left - box.left, y: g.top - box.top, width: g.width, height: g.height },
  });
  return drawToDataUrl(video, rect.x, rect.y, rect.width, rect.height);
};

// Shrink an uploaded photo; phone photos are often 4000px+ wide.
export const shrinkImage = async (dataUrl) => {
  const img = await loadImage(dataUrl);
  return drawToDataUrl(img, 0, 0, img.naturalWidth, img.naturalHeight);
};

// Straighten the card inside a photo: cut it out along `corners` (normalised, see
// cardGeometry) and return it as a flat, upright JPEG.
export const flattenCard = async (dataUrl, corners) => {
  const img = await loadImage(dataUrl);
  const srcW = img.naturalWidth;
  const srcH = img.naturalHeight;
  const { width, height } = flatSize(corners, srcW, srcH);

  const srcCanvas = document.createElement('canvas');
  srcCanvas.width = srcW;
  srcCanvas.height = srcH;
  const srcCtx = srcCanvas.getContext('2d');
  srcCtx.drawImage(img, 0, 0);
  const srcData = srcCtx.getImageData(0, 0, srcW, srcH).data;

  const flatCorners = [
    { x: 0, y: 0 },
    { x: width - 1, y: 0 },
    { x: width - 1, y: height - 1 },
    { x: 0, y: height - 1 },
  ];
  const photoCorners = corners.map((c) => ({ x: c.x * (srcW - 1), y: c.y * (srcH - 1) }));
  const h = solveHomography(flatCorners, photoCorners);
  if (!h) throw new Error('Invalid crop');

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const flat = new ImageData(autoLevels(warpPixels(srcData, srcW, srcH, width, height, h)), width, height);
  ctx.putImageData(flat, 0, 0);
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
};

// Rotate clockwise by 90, 180 or 270 degrees.
export const rotateImage = async (dataUrl, degrees) => {
  const turns = (((degrees / 90) % 4) + 4) % 4;
  if (!turns) return dataUrl;

  const img = await loadImage(dataUrl);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const canvas = document.createElement('canvas');
  canvas.width = turns % 2 ? h : w;
  canvas.height = turns % 2 ? w : h;
  const ctx = canvas.getContext('2d');
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((turns * Math.PI) / 2);
  ctx.drawImage(img, -w / 2, -h / 2);
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
};
